// =====================================================
// Exportación del IIL (Informe Inteligente de Licitación) a Word.
// Genera un documento HTML compatible con Word (.doc) a partir del
// JSON de análisis. Sin dependencias externas, descarga directa.
// =====================================================

const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const REC_LABEL = {
  presentarse: 'Presentarse',
  presentarse_con_condiciones: 'Presentarse con condiciones',
  no_presentarse: 'No presentarse',
};

function h2(n, title) { return `<h2>§${n}. ${esc(title)}</h2>`; }
function p(text) { return text ? `<p>${esc(text)}</p>` : ''; }

function list(items) {
  if (!items || !items.length) return '';
  return `<ul>${items.map((i) => `<li>${esc(i)}</li>`).join('')}</ul>`;
}

function table(headers, rows) {
  if (!rows || !rows.length) return '';
  const head = `<tr>${headers.map((h) => `<th>${esc(h)}</th>`).join('')}</tr>`;
  const body = rows.map((r) => `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`).join('');
  return `<table border="1" cellspacing="0" cellpadding="6">${head}${body}</table>`;
}

function mdToHtml(text) {
  if (!text || typeof text !== 'string') return '';
  const out = [];
  let inList = false;
  const inline = (s) => esc(s).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  for (const raw of text.split('\n')) {
    const l = raw.trim();
    if (!l) { if (inList) { out.push('</ul>'); inList = false; } continue; }
    const h = l.match(/^(#{1,4})\s+(.*)$/);
    if (h) { if (inList) { out.push('</ul>'); inList = false; } out.push(`<h3>${inline(h[2])}</h3>`); continue; }
    const li = l.match(/^[-*]\s+(.*)$/);
    if (li) { if (!inList) { out.push('<ul>'); inList = true; } out.push(`<li>${inline(li[1])}</li>`); continue; }
    if (inList) { out.push('</ul>'); inList = false; }
    out.push(`<p>${inline(l)}</p>`);
  }
  if (inList) out.push('</ul>');
  return out.join('');
}

export function buildIILHtml(bid, a) {
  if (!a) return '<p>No hay análisis disponible.</p>';
  const parts = [];

  // Resumen ejecutivo (markdown)
  if (a.executiveReport) {
    parts.push('<h2>Resumen ejecutivo</h2>');
    parts.push(mdToHtml(a.executiveReport));
  }


  // §1 Snapshot
  if (a.snapshot) {
    const s = a.snapshot;
    parts.push(h2(1, 'Snapshot decisional'));
    if (s.recommendation) parts.push(`<p><strong>Recomendación:</strong> ${esc(REC_LABEL[s.recommendation] || s.recommendation)}${s.confidence != null ? ` (confianza ${esc(s.confidence)}%)` : ''}</p>`);
    parts.push(p(s.rationale));
    if (s.kpis?.length) parts.push(table(['Indicador', 'Valor', 'Detalle'], s.kpis.map((k) => [k.label, k.value, k.sub])));
    if (s.strengths?.length) { parts.push('<h3>Fortalezas</h3>'); parts.push(list(s.strengths)); }
    if (s.attention?.length) { parts.push('<h3>Puntos de atención</h3>'); parts.push(list(s.attention)); }
  }

  // §2 General
  if (a.general) {
    const g = a.general;
    parts.push(h2(2, 'Información general'));
    if (g.fields?.length) parts.push(table(['Campo', 'Valor'], g.fields.map((f) => [f.label, f.value])));
    parts.push(p(g.lots));
    parts.push(p(g.contractImplication));
  }

  // §3 Económico
  if (a.economic) {
    const e = a.economic;
    parts.push(h2(3, 'Análisis económico'));
    if (e.breakdown?.length) parts.push(table(['Concepto', 'Importe', '%'], e.breakdown.map((b) => [b.concept, b.amount, b.pct])));
    if (e.calculations?.length) parts.push(table(['Cálculo', 'Fórmula', 'Resultado'], e.calculations.map((c) => [c.name, c.formula, c.result])));
    parts.push(p(e.abnormalLow));
    parts.push(p(e.strategy));
  }

  // §4 Solvencia
  if (a.solvency) {
    const s = a.solvency;
    parts.push(h2(4, 'Requisitos de solvencia'));
    if (s.economic?.length) { parts.push('<h3>Económica</h3>'); parts.push(list(s.economic)); }
    if (s.technical?.length) { parts.push('<h3>Técnica</h3>'); parts.push(list(s.technical)); }
    if (s.professional?.length) parts.push(table(['Perfil', 'Requisito'], s.professional.map((x) => [x.role, x.requirement])));
    if (s.specialConditions?.length) parts.push(table(['Condición', 'Nivel', 'Penalización', 'Esencial', 'Implicación'], s.specialConditions.map((x) => [x.title, x.level, x.penalty, x.essential ? 'Sí' : 'No', x.implication])));
  }

  // §5 Servicio
  if (a.service) {
    const v = a.service;
    parts.push(h2(5, 'Descripción del servicio'));
    if (v.activities?.length) parts.push(table(['Actividad', 'Categoría', 'Horas', 'Entregables'], v.activities.map((x) => [x.name, x.category, x.hours, x.deliverables])));
    parts.push(p(v.workload));
    parts.push(p(v.hiddenLoad));
  }

  // §6 Equipo
  if (a.team) {
    const t = a.team;
    parts.push(h2(6, 'Equipo requerido'));
    parts.push(p(t.summary));
    if (t.profiles?.length) parts.push(table(['Perfil', 'Nº', 'Requisito', 'Dedicación', 'Coste/h máx.'], t.profiles.map((x) => [x.role, x.count, x.requirement, x.dedication, x.maxRate])));
    if (t.coverage?.length) parts.push(table(['Perfil', 'Asignadas', 'Necesarias', 'Cobertura', 'Riesgo'], t.coverage.map((x) => [x.profile, x.assigned, x.needed, x.coverage, x.risk])));
    parts.push(p(t.scalability));
  }

  // §7 Criterios
  if (a.criteria) {
    const c = a.criteria;
    parts.push(h2(7, 'Criterios de valoración'));
    parts.push(p(c.distribution));
    if (c.subjective?.length) { parts.push('<h3>Sobre 2 — Juicio de valor</h3>'); parts.push(table(['Criterio', 'Puntos', 'Cómo puntuar', 'Recomendación'], c.subjective.map((x) => [x.name, x.points, x.howToScore, x.recommendation]))); }
    if (c.automatic?.length) { parts.push('<h3>Sobre 3 — Fórmula automática</h3>'); parts.push(table(['Criterio', 'Puntos', 'Fórmula', 'Recomendación'], c.automatic.map((x) => [x.name, x.points, x.formula, x.recommendation]))); }
    if (c.priceScenarios?.length) { parts.push('<h3>Escenarios de precio</h3>'); parts.push(table(['Escenario', 'Puntos', 'Margen', 'Nota'], c.priceScenarios.map((x) => [x.scenario, x.points, x.margin, x.note]))); }
    if (c.roi?.length) { parts.push('<h3>ROI de mejoras</h3>'); parts.push(table(['Mejora', 'Coste', 'Puntos', 'ROI'], c.roi.map((x) => [x.improvement, x.cost, x.points, x.roi]))); }
  }

  // §8 Documentación
  if (a.documents) {
    const d = a.documents;
    parts.push(h2(8, 'Documentación a presentar'));
    if (d.contaminationWarning) parts.push(p(d.contaminationWarning));
    if (d.envelope1?.length) { parts.push('<h3>Sobre 1</h3>'); parts.push(list(d.envelope1)); }
    if (d.envelope2?.length) { parts.push('<h3>Sobre 2</h3>'); parts.push(list(d.envelope2)); }
    if (d.envelope3?.length) { parts.push('<h3>Sobre 3</h3>'); parts.push(list(d.envelope3)); }
    if (d.criticalDates?.length) parts.push(table(['Hito', 'Plazo', 'Implicación'], d.criticalDates.map((x) => [x.milestone, x.date, x.implication])));
  }

  // §9 Compromisos
  if (a.commitments) {
    const c = a.commitments;
    parts.push(h2(9, 'Compromisos cuantificables'));
    if (c.warning) parts.push(p(c.warning));
    if (c.items?.length) parts.push(table(['Compromiso', 'Mínimo', 'Recomendación', 'Máximo', 'Puntos', 'Riesgo'], c.items.map((x) => [x.commitment, x.min, x.recommendation, x.max, x.points, x.risk])));
  }

  // §10 Riesgos
  if (a.risks) {
    const r = a.risks;
    parts.push(h2(10, 'Análisis de riesgos'));
    if (r.items?.length) parts.push(table(['Riesgo', 'Probabilidad', 'Impacto', 'Importe', 'Mitigación'], r.items.map((x) => [x.risk, x.probability, x.impact, x.impactValue, x.mitigation])));
    parts.push(p(r.maxExposure && `Exposición máxima teórica: ${r.maxExposure}`));
  }

  // §11 Viabilidad
  if (a.viability) {
    const v = a.viability;
    parts.push(h2(11, 'Viabilidad operativa'));
    parts.push(p(v.equation));
    if (v.coverage?.length) parts.push(table(['Bloque', 'Necesarias', 'Disponibles', 'Cobertura'], v.coverage.map((x) => [x.block, x.needed, x.available, x.coverage])));
    parts.push(p(v.bottlenecks));
    if (v.verdict?.length) parts.push(table(['Dimensión', 'Estado', 'Comentario'], v.verdict.map((x) => [x.dimension, x.status, x.comment])));
  }

  // §12 Evidencias y citas textuales
  if (Array.isArray(a.evidence) && a.evidence.length) {
    parts.push(h2(12, 'Evidencias y citas textuales'));
    parts.push(table(['Sección', 'Documento', 'Cita'], a.evidence.map((e) => [e.section, e.source, e.quote])));
  }


  const style = `
    <style>
      body { font-family: 'Calibri', 'Segoe UI', sans-serif; color:#1a1a1a; font-size:11pt; }
      h1 { font-size:20pt; margin:0 0 4pt; }
      h2 { font-size:14pt; border-bottom:1px solid #888; padding-bottom:3pt; margin-top:18pt; }
      h3 { font-size:12pt; margin-top:12pt; }
      table { width:100%; border-collapse:collapse; margin:8pt 0; font-size:10pt; }
      th { background:#f0f0f0; text-align:left; }
      .meta { color:#555; font-size:10pt; margin-bottom:12pt; }
    </style>`;

  const header = `
    <h1>Informe Inteligente de Licitación</h1>
    <div class="meta">
      ${esc(bid?.title || '')}${bid?.entity ? ` · ${esc(bid.entity)}` : ''}${bid?.code ? ` · ${esc(bid.code)}` : ''}<br/>
      Generado por Lizzi${a.analysis_model ? ` (${esc(a.analysis_model)})` : ''} el ${new Date().toLocaleDateString('es-ES')}
    </div>`;

  return `<!DOCTYPE html><html><head><meta charset="utf-8">${style}</head><body>${header}${parts.join('\n')}</body></html>`;
}

export function downloadIILWord(bid, analysis) {
  const html = buildIILHtml(bid, analysis);
  const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const name = (bid?.title || 'licitacion').replace(/[^\w\-]+/g, '_').slice(0, 60);
  a.href = url;
  a.download = `IIL_${name}.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
